import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Slider, Select, Switch, InputNumber, Collapse, theme, ConfigProvider } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { IconChevronDown } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import TaxonomyPicker from './TaxonomyPicker';
import { IAccessorySearchFilters } from '../interfaces/heavyDutyMachinery/IMachineryAccessory';
import { buildCouplerTypeGroupedOptions } from '../constants/machineryAccessorySpecOptions';
import { TRANSLATIONS } from '../constants/translations';
import { translate } from '../helpers/translationHelper';
import { filterSliderTheme } from '../constants/filterSliderTheme';
import { useAppBreakpoint } from '../hooks/useAppBreakpoint';

const { Text } = Typography;

const PRICE_PER_DAY_RANGE: [number, number] = [0, 300];

interface AccessoryFiltersModalProps {
  open: boolean;
  onClose: () => void;
  filters: IAccessorySearchFilters;
  totalCount: number;
  onChange: (patch: Partial<IAccessorySearchFilters>) => void;
  onClear: () => void;
}

/**
 * Accessory tab's filters overlay on /explore's mobile map -- same theme,
 * layout, and mobile-fullscreen/desktop-Modal fork as FiltersModal
 * (Machinery's own) and OperatorFiltersModal. Everything a host can fill in
 * when creating an accessory (see MachineryAccessory's own doc comment)
 * lives here EXCEPT AccessoryType (that one's driven by the top search
 * bar's own "Tipo de accesorio" field -- see HomeSearchBar's own
 * resolveCategoryName doc comment, not this modal): CompatibleCategories
 * (which machine it fits, same Machinery.Category vocabulary/icons as every
 * other filter modal), a PricePerDay range slider, CouplerType, a
 * "machine weight" single-value match against the accessory's own weight
 * range, a RequiresHydraulics toggle, and a secondary/collapsed Dimensions
 * section (working width/capacity/own weight ranges) -- deliberately not
 * every hydraulic sub-field (flow/pressure/lines/connector type), too
 * niche for a first version of these filters. No facet counts -- same
 * reasoning as OperatorFiltersModal's own doc comment,
 * MachineryAccessoryController.SearchAccessories builds no facets.
 */
const AccessoryFiltersModal: React.FC<AccessoryFiltersModalProps> = ({ open, onClose, filters, totalCount, onChange, onClear }) => {
  const { t } = useTranslation();
  const { token } = theme.useToken();
  const { isMobile } = useAppBreakpoint();

  const [priceDisplay, setPriceDisplay] = useState<[number, number]>([
    filters.minPricePerDay ?? PRICE_PER_DAY_RANGE[0],
    filters.maxPricePerDay ?? PRICE_PER_DAY_RANGE[1],
  ]);
  useEffect(() => {
    setPriceDisplay([filters.minPricePerDay ?? PRICE_PER_DAY_RANGE[0], filters.maxPricePerDay ?? PRICE_PER_DAY_RANGE[1]]);
  }, [filters.minPricePerDay, filters.maxPricePerDay]);

  const renderCategoryGrid = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SPACE_CATEGORY_LABEL)}
      </Text>
      <TaxonomyPicker
        multiple
        value={filters.category ?? []}
        onChange={(next) => onChange({ category: next as string[] })}
      />
    </div>
  );

  // Same curated grouped-by-brand/model CouplerType vocabulary the create/
  // edit form uses -- see buildCouplerTypeGroupedOptions's own doc comment.
  const renderCouplerTypeFilter = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_COUPLER_TYPE_LABEL)}
      </Text>
      <Select
        allowClear
        style={{ width: '100%' }}
        options={buildCouplerTypeGroupedOptions(t)}
        value={filters.couplerType}
        onChange={(value) => onChange({ couplerType: value })}
      />
    </div>
  );

  // Toggling off clears the filter entirely (shows every accessory again)
  // rather than flipping to "only non-hydraulic" -- same "undefined means
  // don't care" convention as every other optional filter here.
  const renderRequiresHydraulicsFilter = () => (
    <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
      <Switch checked={!!filters.requiresHydraulics} onChange={(checked) => onChange({ requiresHydraulics: checked ? true : undefined })} />
      <Text>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_REQUIRES_HYDRAULICS_LABEL)}</Text>
    </div>
  );

  // "I have a machine of this many tons -- what fits it?" -- see
  // AccessorySearchRequest.MachineWeightTons's own doc comment (API repo)
  // for the bracket-match semantics against the accessory's own weight range.
  const renderMachineWeightFilter = () => (
    <div style={{ marginBottom: 20 }}>
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_FILTER_MACHINE_WEIGHT_LABEL)}
      </Text>
      <InputNumber
        min={0}
        step={0.5}
        style={{ width: '100%' }}
        value={filters.machineWeightTons}
        onChange={(value) => onChange({ machineWeightTons: value ?? undefined })}
      />
    </div>
  );

  const renderRangeRow = (label: string, minValue: number | undefined, maxValue: number | undefined, onMinChange: (v?: number) => void, onMaxChange: (v?: number) => void) => (
    <div style={{ marginBottom: 16 }}>
      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 6 }}>
        {label}
      </Text>
      <div style={{ display: 'flex', gap: 8 }}>
        <InputNumber
          min={0}
          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MIN_PLACEHOLDER)}
          style={{ flex: 1 }}
          value={minValue}
          onChange={(v) => onMinChange(v ?? undefined)}
        />
        <InputNumber
          min={0}
          placeholder={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_MAX_PLACEHOLDER)}
          style={{ flex: 1 }}
          value={maxValue}
          onChange={(v) => onMaxChange(v ?? undefined)}
        />
      </div>
    </div>
  );

  // Secondary/collapsed by default -- same "don't overwhelm the modal with
  // niche filters" reasoning as this whole round's own design discussion:
  // dimensions matter less than price/coupler/machine weight above.
  const renderDimensionsSection = () => (
    <Collapse
      ghost
      style={{ marginBottom: 20 }}
      expandIconPosition="end"
      expandIcon={({ isActive }) => (
        <IconChevronDown size={18} color={token.colorTextSecondary} style={{ transform: isActive ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 200ms ease' }} />
      )}
      items={[
        {
          key: 'dimensions',
          label: <Text strong>{translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_DIMENSIONS_SECTION_TITLE)}</Text>,
          children: (
            <>
              {renderRangeRow(
                translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_WORKING_WIDTH_LABEL),
                filters.minWorkingWidthMm,
                filters.maxWorkingWidthMm,
                (v) => onChange({ minWorkingWidthMm: v }),
                (v) => onChange({ maxWorkingWidthMm: v })
              )}
              {renderRangeRow(
                translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_CAPACITY_LABEL),
                filters.minCapacityLiters,
                filters.maxCapacityLiters,
                (v) => onChange({ minCapacityLiters: v }),
                (v) => onChange({ maxCapacityLiters: v })
              )}
              {renderRangeRow(
                translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_OWN_WEIGHT_LABEL),
                filters.minOwnWeightKg,
                filters.maxOwnWeightKg,
                (v) => onChange({ minOwnWeightKg: v }),
                (v) => onChange({ maxOwnWeightKg: v })
              )}
            </>
          ),
        },
      ]}
    />
  );

  const renderPriceSlider = () => (
    <ConfigProvider theme={filterSliderTheme}>
      <div style={{ marginBottom: 24 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_ACCESSORY_PRICE_PER_DAY_LABEL)}
        </Text>
        <Slider
          range
          className="heavyDutyMachinery-filter-slider"
          min={PRICE_PER_DAY_RANGE[0]}
          max={PRICE_PER_DAY_RANGE[1]}
          value={priceDisplay}
          tooltip={{ formatter: (value) => (value != null ? `${value}€` : '') }}
          onChange={(value) => setPriceDisplay(value as [number, number])}
          onChangeComplete={(value) => {
            const [min, max] = value as [number, number];
            onChange({
              minPricePerDay: min === PRICE_PER_DAY_RANGE[0] ? undefined : min,
              maxPricePerDay: max === PRICE_PER_DAY_RANGE[1] ? undefined : max,
            });
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Text type="secondary">{priceDisplay[0]}€</Text>
          <Text type="secondary">{priceDisplay[1]}€</Text>
        </div>
      </div>
    </ConfigProvider>
  );

  const renderShowResultsButton = (style?: React.CSSProperties) => (
    <Button type="primary" onClick={onClose} style={{ borderRadius: 999, ...style }}>
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_SHOW_RESULTS_BUTTON)} ({totalCount})
    </Button>
  );

  const renderClearFiltersButton = () => (
    <button
      type="button"
      onClick={onClear}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        minHeight: 44,
        padding: '0 4px',
        marginLeft: -4,
        textDecoration: 'underline',
        color: token.colorTextSecondary,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        font: 'inherit',
      }}
    >
      {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_CLEAR_FILTERS)}
    </button>
  );

  if (isMobile) {
    return (
      <AnimatePresence>
        {open && (
          <motion.div
            key="accessory-filters-overlay"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            style={{ position: 'fixed', inset: 0, zIndex: 2000, background: token.colorBgContainer, display: 'flex', flexDirection: 'column' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', flexShrink: 0 }}>
              <Text strong style={{ fontSize: 16 }}>
                {translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
              </Text>
              <button
                type="button"
                aria-label={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_OVERLAY_CLOSE)}
                onClick={onClose}
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: '50%',
                  border: 'none',
                  background: token.colorFillTertiary,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  flexShrink: 0,
                }}
              >
                <CloseOutlined style={{ fontSize: 16 }} />
              </button>
            </div>

            <div className="no-scrollbar" style={{ flex: 1, overflowY: 'auto', padding: '0 20px', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
              {renderPriceSlider()}
              <>
                {renderCouplerTypeFilter()}
                {renderMachineWeightFilter()}
                {renderRequiresHydraulicsFilter()}
              </>
              {renderCategoryGrid()}
              {renderDimensionsSection()}
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 12,
                padding: '16px 20px',
                paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                flexShrink: 0,
              }}
            >
              {renderClearFiltersButton()}
              {renderShowResultsButton()}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width="min(760px, 92vw)"
      transitionName="filters-slide"
      maskTransitionName="filters-fade"
      title={translate(t, TRANSLATIONS.HEAVY_DUTY_MACHINERY_SEARCH_FILTERS_TITLE)}
      classNames={{ body: 'no-scrollbar' }}
      styles={{
        content: { borderRadius: 20, border: '0.5px solid rgba(0,0,0,0.08)', boxShadow: '0 8px 24px rgba(0,0,0,0.08)', overflow: 'hidden' },
        body: { maxHeight: '65vh', overflowY: 'auto', padding: '0 28px', scrollbarWidth: 'none', msOverflowStyle: 'none' },
      }}
    >
      {renderPriceSlider()}
      <>
        {renderCouplerTypeFilter()}
        {renderMachineWeightFilter()}
        {renderRequiresHydraulicsFilter()}
      </>
      {renderCategoryGrid()}
      {renderDimensionsSection()}

      <div
        style={{
          position: 'sticky',
          bottom: 0,
          background: token.colorBgContainer,
          margin: '16px -28px 0',
          padding: '16px 28px',
          borderTop: `1px solid ${token.colorBorderSecondary}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {renderClearFiltersButton()}
        {renderShowResultsButton()}
      </div>
    </Modal>
  );
};

export default AccessoryFiltersModal;
